// Cross-cutting constants shared by the api, CLI, and store. Single source of
// truth so adding/renaming an org or status is a one-line change, not a hunt.

import { TaskStatus } from "./types.js";

// ── Orgs ─────────────────────────────────────────────────────────────────────
// Every hub agent must belong to exactly one of these. Generic, org-less agents
// are rejected at registration.
export enum Org {
  Incode = "incode",
  Metamap = "metamap",
  Aid = "aid",
}

/** Every org, as an array (for iteration / validation). */
export const ORGS: readonly Org[] = Object.values(Org);

/**
 * The repository that IS the served knowledge base — its content is the `system/`
 * tree the app clones at startup. An agent whose working repo is this one is
 * therefore scoped to the whole corpus rather than to one repo's slice of it:
 * that is not a special case in the authorization rule, it is the same "an agent
 * reads its own repo" rule applied to the repo that happens to contain everything.
 */
export const KNOWLEDGE_BASE_REPO = "omni-system-knowledge-base";

const ORG_SET: ReadonlySet<string> = new Set<string>(ORGS);
/** Type guard: is `value` one of the documented orgs? */
export function isOrg(value: string | null | undefined): value is Org {
  return value != null && ORG_SET.has(value);
}

// ── Per-org code host + credentials ────────────────────────────────────────────
// The single source of truth for where each org's repos live, how their files are
// browsable, the local mirror-group directory name, and — crucially — the env var
// that carries that org's repo credential. Every org has its OWN token, managed as
// a GitHub repo secret and synced to the deploy (see .github/deploy-secrets.yml): Incode and
// MetaMap on GitHub, authenticid on Bitbucket. This replaces the previously scattered
// owner/host/dir maps (repo-clone OWNER_FOR_ORG, agents HOSTS, repos ORG_DIR).
export interface OrgHost {
  /** Code host. GitHub uses /blob/ + /tree/ URLs; Bitbucket uses /src/. */
  readonly kind: "github" | "bitbucket";
  /** GitHub owner or Bitbucket workspace (e.g. "IncodeTechnologies", "authenticidco"). */
  readonly owner: string;
  /** HTTPS base for browse/source links. */
  readonly base: string;
  /** Local mirror-group directory name (matches sync-repos.sh group_for()). */
  readonly dir: string;
  /** Env var holding this org's repo credential (GitHub PAT / Bitbucket API token).
   *  GH_ (not GITHUB_) prefix: GitHub Actions forbids secret names starting with GITHUB_. */
  readonly tokenEnv: string;
  /** Env var holding the identity paired with `tokenEnv` for the REST API's Basic
   *  auth. Bitbucket Atlassian API tokens authenticate the REST API as `email:token`,
   *  so aid sets this to its account EMAIL. Undefined for GitHub (Octokit uses the
   *  token alone). */
  readonly apiUserEnv?: string;
  /** Env var holding the identity paired with `tokenEnv` for git-over-HTTPS Basic
   *  auth. Bitbucket API tokens authenticate git as `username:token`, so aid sets
   *  this to its Bitbucket USERNAME (distinct from the API email). Undefined for
   *  GitHub (gh authenticates with the token alone). */
  readonly gitUserEnv?: string;
  /** Env var holding this org's DevHub-publish credential — a token DISTINCT from
   *  `tokenEnv` so opening docs PRs can be scoped to just the target docs repos,
   *  independent of the broader repo-cloning / knowledge-base token. Only orgs that
   *  run a DevHub docs site set this (currently just Incode); it never falls back to
   *  `tokenEnv`, so publish stays off for an org until its own token is set. */
  readonly devhubTokenEnv?: string;
  /** Env var holding this org's write credential for the KNOWLEDGE-BASE repo, split
   *  out from `devhubTokenEnv` so the two corpus repos can carry independently-scoped
   *  tokens across the DevHub repo migration: `devhubTokenEnv` writes the devhub repo,
   *  this one writes omni-system-knowledge-base. OPTIONAL — unset falls back to
   *  `devhubTokenEnv` (see resolvePublishAuth), which is the pre-split behaviour, so
   *  adding it is opt-in and its absence cannot break publishing. */
  readonly kbWriteTokenEnv?: string;
}

export const ORG_HOSTS: Record<Org, OrgHost> = {
  [Org.Incode]: {
    kind: "github",
    owner: "IncodeTechnologies",
    base: "https://github.com/IncodeTechnologies",
    dir: "incode",
    tokenEnv: "INCODE_GH_TOKEN",
    devhubTokenEnv: "INCODE_DEVHUB_GH_TOKEN",
    // Write credential for the KNOWLEDGE-BASE repo specifically. Split out from
    // devhubTokenEnv so the two corpus repos can hold independently-scoped tokens
    // across the DevHub repo migration: devhubTokenEnv writes
    // IncodeTechnologies/devhub, this one writes omni-system-knowledge-base.
    // Falls back to devhubTokenEnv when unset (see resolvePublishAuth), so adding
    // it is optional and removing it cannot break publishing.
    kbWriteTokenEnv: "INCODE_KNOWLEDGE_BASE_GH_TOKEN",
  },
  [Org.Metamap]: {
    kind: "github",
    owner: "IncodeMetaMap",
    base: "https://github.com/IncodeMetaMap",
    dir: "metamap",
    tokenEnv: "METAMAP_GH_TOKEN",
  },
  [Org.Aid]: {
    kind: "bitbucket",
    owner: "authenticidco",
    base: "https://bitbucket.org/authenticidco",
    dir: "aid",
    tokenEnv: "AID_BB_TOKEN",
    apiUserEnv: "AID_BB_EMAIL", // Atlassian API token → REST API Basic auth is email:token
    gitUserEnv: "AID_BB_USER", //  and git-over-HTTPS Basic auth is username:token
  },
};

/** Host descriptor for an org string, or null if it isn't a known org. */
export function orgHost(org: string | null | undefined): OrgHost | null {
  return isOrg(org) ? ORG_HOSTS[org] : null;
}

/** GitHub owner for a GitHub-hosted org (e.g. "incode" → "IncodeTechnologies").
 *  null for Bitbucket-hosted orgs (aid) and unknown orgs — the callers that clone
 *  or open PRs only support GitHub, so this doubles as their "cloneable?" gate. */
export function githubOwnerForOrg(org: string | null | undefined): string | null {
  const host = orgHost(org);
  return host && host.kind === "github" ? host.owner : null;
}

/** The known orgs among `names` (e.g. the content dirs present under system/org/),
 *  in canonical ORGS order. Used to decide which orgs to surface in the UI. */
export function orgsIn(names: readonly string[]): Org[] {
  const present = new Set(names);
  return ORGS.filter((org) => present.has(org));
}

// ── Task statuses ─────────────────────────────────────────────────────────────
// The task board columns, derived from the TaskStatus enum.
export const TASK_STATUSES: readonly TaskStatus[] = Object.values(TaskStatus);

const TASK_STATUS_SET: ReadonlySet<string> = new Set<string>(TASK_STATUSES);
/** Type guard: is `value` a valid task status? */
export function isTaskStatus(value: string | null | undefined): value is TaskStatus {
  return value != null && TASK_STATUS_SET.has(value);
}

// ── Agent liveness ─────────────────────────────────────────────────────────────
// Agents are "online" while they keep calling the api; the reaper flips them
// offline after AGENT_STALE_AFTER_MS of silence, polling every AGENT_REAP_INTERVAL_MS.
export const AGENT_STALE_AFTER_MS = 30_000;
export const AGENT_REAP_INTERVAL_MS = 5_000;
